import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { Prompt, useLocation } from 'react-router-dom';

import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';
import { FlowCardExplorerDialog } from '../flow-card-explorer-dialog/FlowCardExplorerDialog';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useQuery } from '../../../hooks/useQuery';
import { useFlowCardActionsLoading } from '../../../store/flow-cards/useFlowCardActions';
import { useFlowCardConditionsLoading } from '../../../store/flow-cards/useFlowCardConditions';
import { useFlowCardTriggersById } from '../../../store/flow-cards/useFlowCardTriggers';
import { useFlowCardTriggersLoading } from '../../../store/flow-cards/useFlowCardTriggers';
import { TokenContext } from '../TokenContext';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { GradientButton } from '../../../components/buttons/GradientButton';
import { MonoButton } from '../../../components/buttons/MonoButton';
import { Scroll } from '../../../components/common/Scroll';
import { SpinnerFade } from '../../../components/common/SpinnerFade';

import { FlowCardActions } from './FlowCardActions';
import { FlowCardConditions } from './FlowCardConditions';
import { FlowCardTrigger } from './FlowCardTrigger';
import { FlowName } from './FlowName';
import { FlowTestOverlayTrigger } from './FlowTestOverlayTrigger';
import { useFlowEditor, useSetFlowEditor, flowEditor } from './useFlowEditor';
import { flowTest } from './useFlowTest';
import { useHasUnsavedChanges } from './useHasUnsavedChanges';

export function FlowEditorView(props) {
  const { i18n } = useI18n();

  const location = useLocation();
  const params = useQuery();
  const resetId = params.get('id');
  const folderId = params.get('folderId');

  useSetFlowEditor({
    flowId: props.flowId,
    folderId: folderId,
    resetId: resetId,
  });

  const isFlowCardTriggersLoading = useFlowCardTriggersLoading();
  const isFlowCardConditionsLoading = useFlowCardConditionsLoading();
  const isFlowCardActionsLoading = useFlowCardActionsLoading();

  const { flow, initialFlow, saving } = useFlowEditor();
  const tokenContext = useTriggerTokensByKey({ flow });

  const [flowCardPickerState, setFlowCardPickerState] = useState();

  // Can test if there are no tokens or only image tokens.
  const canTestWithoutTokens = Object.values(tokenContext.tokensByKey).every(
    (token) => token.type === 'image',
  );

  const hasUnsavedChanges = useHasUnsavedChanges({ flow, initialFlow });

  function handleNavigateAway(nextLocation, action) {
    if (
      `${nextLocation.pathname}${nextLocation.search}` === `${location.pathname}${location.search}`
    ) {
      return true;
    }

    if (nextLocation.state?.skipUnsavedChangesCheck === true) {
      return true;
    }

    if (hasUnsavedChanges) {
      return i18n.messageFormatter('flow.unsavedChanges');
    }

    return true;
  }

  return (
    <TokenContext.Provider value={tokenContext}>
      {flow != null &&
        isFlowCardTriggersLoading === false &&
        isFlowCardConditionsLoading === false &&
        isFlowCardActionsLoading === false && (
          <Scroll>
            <FlowEditorView.Root>
              <FlowEditorView.Content>
                <FlowEditorView.Header>
                  <FlowName
                    autoFocus={location.state?.focus === 'name'}
                    key={`${flow.id}-${resetId}`}
                    aria-label="Flow name"
                    defaultValue={flow.name}
                    placeholder={i18n.messageFormatter('flow.enterName')}
                    onRequestSave={(value) => {
                      flowEditor.setProperty({ property: 'name', value: value });
                    }}
                    onRequestCancel={() => {
                      flowEditor.setProperty({ property: 'name', value: flow.name });
                    }}
                  />
                </FlowEditorView.Header>
                <FlowEditorView.Center>
                  <FlowCardTrigger
                    trigger={flow.trigger}
                    setFlowCardPickerState={setFlowCardPickerState}
                  />
                  <FlowCardConditions
                    conditions={flow.conditions}
                    setFlowCardPickerState={setFlowCardPickerState}
                  />
                  <FlowCardActions
                    actions={flow.actions}
                    setFlowCardPickerState={setFlowCardPickerState}
                  />

                  <AnimatePresence>
                    {flowCardPickerState != null && (
                      <FlowCardExplorerDialog
                        advanced={false}
                        cardType={flowCardPickerState.type}
                        currentCard={flowCardPickerState.currentCard}
                        onCloseRequest={() => {
                          setFlowCardPickerState(null);
                        }}
                        onCardSelect={(cardKey, card, cardDroptoken) => {
                          const index = flowCardPickerState.cardIndex;
                          const type = flowCardPickerState.type;

                          setFlowCardPickerState(null);
                          switch (type) {
                            case 'trigger':
                              flowEditor.setTrigger({
                                cardKey,
                                cardIndex: index,
                                card,
                                cardDroptoken,
                              });
                              break;
                            case 'condition':
                              flowEditor.setCondition({
                                cardKey,
                                cardIndex: index,
                                card,
                                cardDroptoken,
                              });
                              break;
                            case 'action':
                              flowEditor.setAction({
                                cardKey,
                                cardIndex: index,
                                card,
                                cardDroptoken,
                              });
                              break;
                            default:
                              break;
                          }
                        }}
                      />
                    )}
                  </AnimatePresence>
                </FlowEditorView.Center>
                <FlowEditorView.StickyActions>
                  <FlowEditorView.StickyActionsCenter>
                    <FlowTestOverlayTrigger
                      triggerTokensById={tokenContext.tokensByKey}
                      renderTrigger={(triggerRef, triggerProps, triggerState) => {
                        return (
                          <MonoButton
                            {...triggerProps}
                            ref={triggerRef}
                            styleIconColor="green"
                            onPress={() => {
                              const validation = flowEditor.validate({ type: 'test' });
                              const isValid = Object.keys(validation ?? {}).length === 0;

                              if (isValid === false) {
                                const message = Object.entries(validation)
                                  .map(([key, value]) => {
                                    if (typeof value === 'object' && value != null) {
                                      return i18n.messageFormatter(value.type, {
                                        argTitle: value.argTitle,
                                        cardTitle: value.cardTitle,
                                      });
                                    }
                                    return value;
                                  })
                                  .join('\n');
                                ToastManager.addError({ message: message });
                                return;
                              }

                              if (canTestWithoutTokens) {
                                flowTest
                                  .testFlow()
                                  .then(() => {
                                    ToastManager.add({
                                      icon: glyph.system.flow,
                                      message: i18n.messageFormatter('flow.startTest'),
                                    });
                                  })
                                  .catch(ToastManager.handleError);
                                return;
                              }

                              triggerState.open();
                            }}
                          >
                            <MonoButton.Icon
                              size={theme.icon.size_default}
                              source={glyph.core.play}
                            />
                            <MonoButton.Text>{i18n.messageFormatter('flow.test')}</MonoButton.Text>
                          </MonoButton>
                        );
                      }}
                    />

                    <GradientButton
                      isDisabled={saving}
                      onPress={() => {
                        if (saving) return;

                        const validation = flowEditor.validate();
                        const isValid = Object.keys(validation ?? {}).length === 0;

                        if (isValid === false) {
                          const message = Object.entries(validation)
                            .map(([key, value]) => {
                              if (typeof value === 'object' && value != null) {
                                return i18n.messageFormatter(value.type, {
                                  argTitle: value.argTitle,
                                  cardTitle: value.cardTitle,
                                });
                              }
                              return value;
                            })
                            .join('\n');
                          ToastManager.addError({ message: message });
                          return;
                        }

                        if (flow.id === 'create') {
                          flowEditor
                            .createFlow()
                            .then((result) => {
                              RouteManager.toFlow(result.id, {
                                skipUnsavedChangesCheck: true,
                              });
                              ToastManager.add({
                                icon: glyph.core.checkmarkCircle,
                                iconColor: theme.color.highlight,
                                message: i18n.messageFormatter('flow.saveSuccess'),
                              });
                            })
                            .catch(ToastManager.handleError);
                        } else {
                          flowEditor
                            .saveFlow()
                            .then(() => {
                              ToastManager.add({
                                icon: glyph.core.checkmarkCircle,
                                iconColor: theme.color.highlight,
                                message: i18n.messageFormatter('flow.saveSuccess'),
                              });
                            })
                            .catch(ToastManager.handleError);
                        }
                      }}
                    >
                      <SpinnerFade
                        url={glyph.core.checkmark}
                        size={theme.icon.size_default}
                        color={theme.color.white}
                        isActive={saving === true}
                      >
                        <GradientButton.Icon
                          source={glyph.core.checkmark}
                          size={theme.icon.size_default}
                          color={theme.color.white}
                        />
                      </SpinnerFade>
                      <GradientButton.Text>
                        {/* {saving === true
                        ? `${i18n.messageFormatter('common.saving')}...`
                        : i18n.messageFormatter('common.save')} */}
                        {i18n.messageFormatter('common.save')}
                      </GradientButton.Text>
                    </GradientButton>
                  </FlowEditorView.StickyActionsCenter>
                </FlowEditorView.StickyActions>
              </FlowEditorView.Content>
            </FlowEditorView.Root>
          </Scroll>
        )}

      <Prompt message={handleNavigateAway} />
    </TokenContext.Provider>
  );
}

function useTriggerTokensByKey({ flow }) {
  const triggerData = flow?.trigger;
  const flowCardTriggers = useFlowCardTriggersById();
  const triggerCard = flowCardTriggers.byId?.[triggerData?.id];

  return useMemo(() => {
    const tokens = triggerCard?.tokens ?? [];

    const tokensByKey = {};

    for (const token of tokens) {
      tokensByKey[token.id] = token;
    }

    return {
      tokensByKey,
    };
  }, [triggerCard]);
}

FlowEditorView.Root = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  padding: ${su(6, 3)};
`;

FlowEditorView.Content = styled.div`
  width: 100%;
`;

FlowEditorView.Header = styled.header`
  min-width: 340px;
  width: 100%;
  max-width: 600px;
  text-align: center;
  margin: 0 auto;
`;

FlowEditorView.Center = styled.div`
  width: 340px;
  margin: 0 auto;
`;

FlowEditorView.StickyActions = styled.div`
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  background: ${theme.color.body};
  padding: ${su(2)} 0;
  z-index: 10;
  width: 100%;
  transition: ${motion.transition.micro};

  &::before {
    content: '';
    position: absolute;
    background: radial-gradient(ellipse at 50% 100%, ${theme.color.mono_o_10}, transparent 66%);
    height: ${su(2)};
    bottom: 100%;
    width: 100%;
    left: 0;
    opacity: 0;
    transition: opacity 200ms ease-in-out;

    .has-scroll-state:not(.is-scroll-bottom) & {
      opacity: 1;
    }
  }
`;

FlowEditorView.StickyActionsCenter = styled.div`
  display: flex;
  justify-content: space-evenly;
  max-width: 340px;
  margin: 0 auto;

  > * {
    width: 100%;
  }

  & > :first-of-type {
    margin-inline-end: ${su(2)};
  }
`;
